import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../../common/SafeIcon';
import AuthLayout from '../../components/AuthLayout';
import supabase from '../../supabase/supabase';

const { FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle } = FiIcons;
const { FaGoogle, FaLinkedinIn } = FaIcons;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      navigate('/dashboard');
    }
  };

  const handleOAuth = async (provider) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/#/dashboard`
        }
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <AuthLayout 
      title="Welcome Back" 
      subtitle="Enter your credentials or use social login to access your dashboard."
    >
      <form onSubmit={handleLogin} className="space-y-5">
        {error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-600 text-sm">
            <SafeIcon icon={FiAlertCircle} />
            {error}
          </div>
        )}

        <div>
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Email Address</label>
          <div className="relative">
            <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              required
              type="email"
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">Password</label>
            <Link to="/forgot-password" size="sm" className="text-xs font-bold text-blue-600 hover:underline">Forgot?</Link>
          </div>
          <div className="relative">
            <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              required
              type={showPassword ? "text" : "password"}
              className="w-full pl-12 pr-12 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all text-sm"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
            </button>
          </div>
        </div>

        <button
          disabled={loading}
          className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50 font-ubuntu"
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>

      <div className="my-8 flex items-center gap-4">
        <div className="flex-1 h-px bg-gray-200"></div>
        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Or continue with</span>
        <div className="flex-1 h-px bg-gray-200"></div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => handleOAuth('google')}
          disabled={loading}
          className="flex items-center justify-center gap-3 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all text-sm font-bold text-gray-700 font-ubuntu disabled:opacity-50"
        >
          <SafeIcon icon={FaGoogle} className="text-red-500" />
          Google
        </button>
        <button
          onClick={() => handleOAuth('linkedin')}
          disabled={loading}
          className="flex items-center justify-center gap-3 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all text-sm font-bold text-gray-700 font-ubuntu disabled:opacity-50"
        >
          <SafeIcon icon={FaLinkedinIn} className="text-[#0758D1]" />
          LinkedIn
        </button>
      </div>

      <p className="mt-8 text-center text-sm text-gray-500">
        Don't have an account? <Link to="/signup" className="text-blue-600 font-bold hover:underline">Create one free</Link>
      </p>
    </AuthLayout>
  );
};

export default Login;